import { useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { type InputRef } from 'antd';
import dayjs from 'dayjs';

import { useOutsideClick } from '@/shared/hooks/useOnClickOutside';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { VALIDATION_MESSAGES } from '@/shared/lib/constants/form';
import { addRecentAsset } from '@/shared/lib/storage/recentAssets';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import type { RecentAsset } from '@/shared/types/recentAssets';
import { SearchInput } from '@/shared/ui/SearchInput';

import type { SearchResult } from '@/entities/search';

import { useRecentSearchHistory, useSearchExecution, useSearchInput } from '@/features/dataSearch/model';
import { SearchResultDropdown } from '@/features/dataSearch/ui/SearchResultDropdown';

import styles from './DataSearch.module.css';

export const DataSearch = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const isComposingRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  const searchInput = useSearchInput();
  const history = useRecentSearchHistory();
  const { execute } = useSearchExecution();

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 256,
    warningMessage: VALIDATION_MESSAGES.DATA_SEARCH_MAX_LENGTH,
  });

  useOutsideClick(containerRef, {
    onClickOutside: () => setIsOpen(false),
    ignoreSelectors: [
      '.ant-dropdown',
      '.ant-select-dropdown',
      '.ant-tree-select-dropdown',
      '.ant-tree',
      '.ant-checkbox',
    ],
    enabled: isOpen,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    validateLength(value);
    searchInput.handleChange(value);
  };

  const handleClear = () => {
    searchInput.handleChange('');
    setIsOpen(true);
    inputRef.current?.focus();
    history.refresh();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    history.refresh();
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue) {
      history.addKeyword(trimmedValue);
    }

    execute(trimmedValue);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handlePressEnter = () => {
    if (!isComposingRef.current) {
      handleSearch(searchInput.value);
    }
  };

  const handleSelectSuggestion = (item: SearchResult) => {
    addRecentAsset({
      id: item.id,
      name: item.title,
      type: item.type,
      fqn: item.path,
      viewedAt: dayjs().toISOString(),
      ...(item.projectId && { projectId: item.projectId }),
      ...(item.experimentId && { experimentId: item.experimentId }),
      ...(item.experimentName && { experimentName: item.experimentName }),
    });

    if (item.path.startsWith('/experiments/')) {
      navigate(item.path);
    } else {
      navigate(buildAssetDetailUrl(item.type, item.path, undefined, item.id, item.projectId));
    }
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleSelectKeyword = (keyword: string) => {
    searchInput.setValue(keyword);
    handleSearch(keyword);
  };

  const handleSelectRecentAsset = (asset: RecentAsset) => {
    addRecentAsset({
      id: asset.id,
      name: asset.name,
      type: asset.type,
      fqn: asset.fqn,
      viewedAt: dayjs().toISOString(),
      ...(asset.projectId && { projectId: asset.projectId }),
      ...(asset.experimentId && { experimentId: asset.experimentId }),
      ...(asset.experimentName && { experimentName: asset.experimentName }),
    });

    if (asset.fqn.startsWith('/experiments/')) {
      navigate(asset.fqn);
    } else {
      navigate(buildAssetDetailUrl(asset.type, asset.fqn, undefined, asset.id, asset.projectId));
    }
    setIsOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className={styles.searchContainer} ref={containerRef}>
      <SearchInput
        placeholder="데이터 자산을 검색하세요"
        maxLength={256}
        value={searchInput.value}
        onChange={handleInputChange}
        onClear={handleClear}
        onCompositionStart={() => {
          isComposingRef.current = true;
          handleCompositionStart();
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          handleCompositionEnd();
        }}
        onPressEnter={handlePressEnter}
        onFocus={handleInputFocus}
        className={styles.searchInput}
        allowClear
      />

      {isOpen && (
        <div className={styles.suggestionDropdown} onMouseDown={(e) => e.preventDefault()}>
          <SearchResultDropdown
            debouncedValue={searchInput.debouncedValue}
            recentAssets={history.recentAssets}
            recentKeywords={history.keywords}
            isShowSearchSuggestions={!!searchInput.debouncedValue.trim()}
            onClickRecentAsset={handleSelectRecentAsset}
            onClickKeyword={handleSelectKeyword}
            onClickRemoveKeyword={history.removeKeyword}
            onClickSuggestion={handleSelectSuggestion}
            onClickViewAllResults={() => handleSearch(searchInput.value)}
          />
        </div>
      )}
    </div>
  );
};
